import { useTacticHooks } from '../providers/TacticHooksProvider';
import { QueryOptions } from './types';
import { IAttendanceRequestType, LeaveRequestsParams, parseDateOnlyString } from '@gettactic/api';
import { useQuery } from '@tanstack/react-query';
import { addDays, eachDayOfInterval, format } from 'date-fns';
import { useMemo } from 'react';

export function useAttendanceRequests(
  params: Partial<Omit<LeaveRequestsParams, 'start' | 'end'>> & { start: Date; end: Date; tz: string },
  options: QueryOptions = { keepPreviousData: true, enabled: true }
) {
  const { apiClient } = useTacticHooks();

  const commonParams: LeaveRequestsParams = {
    ...params,
    start: params.start ? format(params.start, 'yyyy-MM-dd') : undefined,
    end: params.end ? format(params.end, 'yyyy-MM-dd') : undefined,
    workPolicyType: params.workPolicyType ? params.workPolicyType : [],
    status: params.status ? params.status : ['pending', 'accepted']
  };

  const dataQuery = useQuery({
    queryKey: ['attendanceRequests', commonParams],

    queryFn: async () => {
      const res = await apiClient.leave.allRequests(commonParams);
      return res.result ? res.result.elements : [];
    },

    ...options
  });
  return dataQuery;
}

export function useAttendanceRequestsWeek(monday: Date, tz: string) {
  const attendanceQuery = useAttendanceRequests({
    start: monday,
    end: addDays(monday, 7),
    tz
  });

  const attendanceByDay = useMemo(() => {
    const requests = attendanceQuery.data ?? [];
    const weekDayRequests: { [date: string]: IAttendanceRequestType } = {};
    requests.forEach((request) => {
      eachDayOfInterval({
        start: parseDateOnlyString(request.starts),
        end: parseDateOnlyString(request.ends)
      }).forEach((date) => {
        // Replace only if the request is newer
        const current = weekDayRequests[format(date, 'yyyy-MM-dd')];
        // we are using the `starts` fields, but we should be using smth like `update`
        if (!current || current.starts > request.starts) {
          weekDayRequests[format(date, 'yyyy-MM-dd')] = request;
        }
      });
    });
    return weekDayRequests;
  }, [attendanceQuery.data]);
  return { attendanceQuery, attendanceByDay };
}
